import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Star, ExternalLink } from 'lucide-react';

interface KocReview {
  id: string;
  name: string;
  handle: string | null;
  quote: string;
  avatar_url: string | null;
  photos: string[];
  post_url: string | null;
  tag: string | null;
}

function KocCard({ review }: { review: KocReview }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const total = review.photos.length;

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  const scrollTo = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(total - 1, i));
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <article className="bg-background rounded-2xl shadow-soft hover:shadow-elevated transition-shadow duration-300 overflow-hidden flex flex-col motion-reduce:transition-none">
      {/* Photo carousel — 4:5 */}
      <div className="relative w-full" style={{ aspectRatio: '4 / 5' }}>
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth motion-reduce:scroll-auto"
        >
          {review.photos.map((src, i) => (
            <div key={i} className="relative flex-none w-full h-full snap-center">
              <img
                src={src}
                alt={`${review.name} 的體驗照片 ${i + 1}／共 ${total} 張`}
                loading="lazy"
                className="w-full h-full object-cover"
                width={480}
                height={600}
              />
            </div>
          ))}
        </div>

        {/* Avatar top-left */}
        {review.avatar_url && (
          <img
            src={review.avatar_url}
            alt={`${review.name} 的大頭照`}
            width={52}
            height={52}
            loading="lazy"
            className="absolute top-3 left-3 w-[52px] h-[52px] rounded-full border-2 border-white shadow-md object-cover z-10"
          />
        )}

        {/* Counter top-right */}
        {total > 1 && (
          <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-black/45 text-white text-xs font-sans-tc tabular-nums">
            {index + 1} / {total}
          </div>
        )}

        {/* Arrow buttons */}
        {total > 1 && (
          <>
            <button
              type="button"
              aria-label="上一張照片"
              onClick={() => scrollTo(index - 1)}
              disabled={index === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center z-10 transition disabled:opacity-30 disabled:cursor-not-allowed motion-reduce:transition-none"
            >
              <ChevronLeft className="w-5 h-5 text-brand-charcoal" strokeWidth={2} />
            </button>
            <button
              type="button"
              aria-label="下一張照片"
              onClick={() => scrollTo(index + 1)}
              disabled={index === total - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center z-10 transition disabled:opacity-30 disabled:cursor-not-allowed motion-reduce:transition-none"
            >
              <ChevronRight className="w-5 h-5 text-brand-charcoal" strokeWidth={2} />
            </button>
          </>
        )}

        {/* Dots bottom */}
        {total > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
            {review.photos.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`跳至第 ${i + 1} 張照片`}
                onClick={() => scrollTo(i)}
                className={`h-2 rounded-full transition-all motion-reduce:transition-none ${
                  i === index ? 'w-5 bg-white' : 'w-2 bg-white/60'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 text-brand-rose" aria-label="5 星評價">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="w-4 h-4 fill-current" />
            ))}
          </div>
          {review.tag && (
            <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full bg-brand-sand text-brand-charcoal text-xs font-sans-tc">
              {review.tag}
            </span>
          )}
        </div>

        <blockquote className="font-serif-tc text-[15px] md:text-base leading-relaxed text-foreground flex-1">
          <span className="text-brand-rose font-serif-tc mr-0.5">「</span>
          {review.quote}
          <span className="text-brand-rose font-serif-tc ml-0.5">」</span>
        </blockquote>

        <div className="flex items-center justify-between pt-2 border-t border-border/40">
          <span className="font-sans-tc text-sm text-foreground">{review.name}</span>
          {review.post_url ? (
            <a
              href={review.post_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-sans-tc text-brand-rose hover:underline"
              aria-label={`查看 ${review.name} 的原文分享（新分頁）`}
            >
              看原文 <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span className="text-xs font-sans-tc text-muted-foreground">真實回饋</span>
          )}
        </div>
      </div>
    </article>
  );
}

export default function KocReviews() {
  const [reviews, setReviews] = useState<KocReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('koc_reviews')
        .select('id,name,handle,quote,avatar_url,photos,post_url,tag')
        .eq('is_active', true)
        .order('sort', { ascending: true });
      if (!error && data) {
        setReviews(
          data.map((r) => ({
            ...r,
            photos: Array.isArray(r.photos) ? (r.photos as string[]) : [],
          })) as KocReview[]
        );
      }
      setLoading(false);
    })();
  }, []);

  if (loading || reviews.length === 0) return null;

  return (
    <section aria-labelledby="koc-reviews-heading" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="text-center mb-10 md:mb-14">
          <p className="font-sans-en text-xs md:text-sm tracking-[0.3em] text-brand-rose mb-3">
            REAL REVIEWS
          </p>
          <h2
            id="koc-reviews-heading"
            className="font-serif-tc text-[28px] md:text-[36px] font-light text-brand-charcoal mb-4 tracking-wide"
          >
            他們洗過之後，這樣說
          </h2>
          <div className="w-12 h-px bg-brand-rose mx-auto mb-5" />
          <p className="font-sans-tc text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
            來自體驗夥伴的真實分享，
            <br className="sm:hidden" />
            每一句都是洗完當下的心得。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {reviews.map((r) => (
            <KocCard key={r.id} review={r} />
          ))}
        </div>

        <p className="text-center mt-10 md:mt-12 font-sans-tc text-xs text-muted-foreground">
          — 以上為 KOC 合作體驗分享，照片與文字經授權使用 —
        </p>
      </div>
    </section>
  );
}
